import { Chip } from '@/shared/ui/components';
import { BIODIRECT_STATUS } from '../../../../config/studyCard';
import type { Study } from './biodirectProgress.types';

/**
 * Maps a Biodirect study's shipment state to the progress label shown on its card. NEW yields preparing for shipment; PENDING reads recipientInformation.latestLabel tracking status to choose delivered to patient, being shipped or still preparing; RUNNING yields delivered to patient; COMPLETED yields being returned only while the sender label is not yet DELIVERED; RETURNED yields device returned; any other status yields null.
 *
 * @param study - The study whose biofluxDirect shipment state is inspected.
 * @returns The shipment progress label, or null when there is nothing to show.
 */
const getBiofluxDirectProgress = (study: Study) => {
  const { biofluxDirect } = study;
  switch (biofluxDirect?.status) {
    case BIODIRECT_STATUS.NEW: {
      return 'Preparing device for shipment';
    }
    case BIODIRECT_STATUS.PENDING: {
      const { recipientInformation } = biofluxDirect;
      if (recipientInformation.latestLabel?.transaction?.trackingStatus) {
        if (recipientInformation.latestLabel.transaction.trackingStatus === 'DELIVERED') {
          return 'Device delivered to patient';
        }
        return 'Device is being shipped';
      }
      return 'Preparing device for shipment';
    }
    case BIODIRECT_STATUS.RUNNING: {
      return 'Device delivered to patient';
    }
    case BIODIRECT_STATUS.COMPLETED: {
      const { senderInformation } = biofluxDirect;
      if (
        senderInformation.latestLabel?.transaction?.trackingStatus &&
        senderInformation.latestLabel.transaction.trackingStatus !== 'DELIVERED'
      ) {
        return 'Device is being returned';
      }
      return null;
    }
    case BIODIRECT_STATUS.RETURNED: {
      return 'Device returned';
    }
    default: {
      return null;
    }
  }
};

/**
 * Renders the Biodirect shipment progress chip of a study. Resolves the label through getBiofluxDirectProgress and returns null when there is none, otherwise wraps the text in a non-closable grey Chip.
 *
 * @param study - The study whose shipment progress is rendered.
 * @returns The progress chip, or null when no label applies.
 */
const biodirectProgressRenderer = (study: Study) => {
  const biodirectProgress = getBiofluxDirectProgress(study);

  if (!biodirectProgress) {
    return null;
  }

  return (
    <Chip className="biodirect-progress" background="#F1F5F8" color="#444F66" closable={false}>
      {biodirectProgress}
    </Chip>
  );
};

export { biodirectProgressRenderer, getBiofluxDirectProgress };
